// This feels like it should just be in TouchControlProvider
// But it also feels like it's too big to be in TouchControlProvider
// In any case, this is kinda yucky and can be done better!

import { now } from 'moment';

const SWIPE_THRESHOLD = 10; // In px
const HOLD_TIME = 1000; // In ms
const HOLD_MAX_DISTANCE = 3; // In px

const PULL_FREQUENCY = 60; // Calls per second
const PULL_INTERVAL = 1000 / PULL_FREQUENCY;

export type Swipe = {
  startX: number;
  startY: number;

  endX: number;
  endY: number;

  deltaX: number;
  deltaY: number;
};

export type Tap = {
  x: number;
  y: number;

  length: number;
};

export type SwipeHandler = (swipe: Swipe) => any;
export type TapHandler = (tap: Tap) => any;

export type EventType =
  | 'swipeGeneric'
  | 'swipeHorizontal'
  | 'swipeVertical'
  | 'pull'
  | 'hold'
  | 'tap';

let touchStartTime = 0;

let holdTimeout = null;
let pullInterval = null;

let down = false;
let startX = 0;
let startY = 0;

let currentX = 0;
let currentY = 0;

let averagedSpeed = 0;

const eventHandlers = {
  swipeGeneric: {} as { [id: string]: SwipeHandler },
  swipeHorizontal: {} as { [id: string]: SwipeHandler },
  swipeVertical: {} as { [id: string]: SwipeHandler },

  pull: {} as { [id: string]: SwipeHandler },

  hold: {} as { [id: string]: TapHandler },
  tap: {} as { [id: string]: TapHandler },
};

function initialize() {
  addListener('swipeGeneric', 'base', handleSwipe);
}

function addListener(eventType: EventType, id: string, callback: SwipeHandler) {
  eventHandlers[eventType][id] = callback;
}

function fireEvent(eventType: EventType, data: any) {
  for (let [id, callback] of Object.entries(eventHandlers[eventType])) {
    callback(data);
  }
}

/*
  Event handler for onTouchStart, to be used in TouchControlProvider
  @param {TouchEvent} ev - The event in question
*/
function handleTouchStart(ev) {
  // Ignore all touches other than the first one (no need for panning or pinching here)
  if (ev.touches.length > 1) {
    down = false;
    return;
  }

  const touch = ev.touches[0];

  startX = touch.clientX;
  startY = touch.clientY;

  currentX = startX;
  currentY = startY;

  averagedSpeed = 0;

  down = true;

  touchStartTime = now();

  holdTimeout = setTimeout(() => {
    if (!down) return;

    const deltaTime = now() - touchStartTime;

    const deltaX = currentX - startX;
    const deltaY = currentY - startY;

    if (Math.abs(deltaX) > HOLD_MAX_DISTANCE || Math.abs(deltaY) > HOLD_MAX_DISTANCE) return;

    down = false;

    const tap: Tap = {
      x: startX,
      y: startY,

      length: deltaTime,
    };

    fireEvent('hold', tap);
  }, HOLD_TIME);

  pullInterval = setInterval(() => {
    if (!down) return;

    const deltaX = currentX - startX;
    const deltaY = currentY - startY;

    if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;

    const pull: Swipe = {
      startX,
      startY,

      endX: currentX,
      endY: currentY,

      deltaX,
      deltaY,
    };

    fireEvent('pull', pull);
  }, PULL_INTERVAL);
}

/*
  Event handler for onTouchEnd, to be used in TouchControlProvider
  @param {TouchEvent} ev - The event in question
*/
function handleTouchEnd(ev) {
  if (holdTimeout) clearTimeout(holdTimeout);
  if (pullInterval) clearInterval(pullInterval);

  down = false;
}

/*
  Event handler for onTouchMove, to be used in TouchControlProvider
  @param {TouchEvent} ev - The event in question
*/
function handleTouchMove(ev) {
  if (!down) return;

  const touch = ev.touches[0];

  let x = touch.clientX;
  let y = touch.clientY;

  const deltaX = x - currentX;
  const deltaY = y - currentY;

  const delta = Math.sqrt(Math.pow(deltaX, 2) + Math.pow(deltaY, 2)); // Basic pythagorean theorem

  // This is kinda yucky, not really speed, might try to improve
  averagedSpeed = (averagedSpeed + delta) / 2;

  currentX = x;
  currentY = y;

  // This is probably not the best way to do this
  // I'd wager averaging the speeds over the course of a few events would be better
  // Could also allow for multiple swipes in the same touch (holding down and moving from side to side)
  // ^ The above is kind of useless but looks cool ;)
  // WIP to do the above
  if (averagedSpeed < SWIPE_THRESHOLD) return;

  const swipe: Swipe = {
    startX: startX,
    startY: startY,

    endX: currentX,
    endY: currentY,

    deltaX: deltaX,
    deltaY: deltaY,
  };

  averagedSpeed = 0;

  fireEvent('swipeGeneric', swipe);
}

let lastSwipeTime = 0;

function handleSwipe(swipe: Swipe) {
  const { deltaX, deltaY } = swipe;

  const deltaTime = now() - lastSwipeTime;
  if (deltaTime < 200) return;

  lastSwipeTime = now();

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    // Horizontal
    fireEvent('swipeHorizontal', swipe);
  } else {
    // Vertical
    fireEvent('swipeVertical', swipe);
  }
}

export { initialize, addListener, handleTouchStart, handleTouchMove, handleTouchEnd };
